import {Injectable} from '@angular/core';
import {Game} from "../interfaces/game";
import {SubmenuItem} from "../interfaces/submenu-item";
import {ItemTag} from "../interfaces/item-tag";
import {ItemTagColor} from "../atoms/item-tag/item-tag-color";
import {PictureQuestion} from "../interfaces/picture-question";
import {ImageService} from "../atoms/image/image.service";
import {LargeMessageInput} from "../interfaces/large-message-input";
import {LargeMessageTheme} from "../enums/large-message-theme";
import {OverlayService} from "../overlay/overlay.service";
import {GameCategoriesSelectorComponent} from "../shared/game-categories-selector/game-categories-selector.component";
import {DataService} from "./data.service";
import {StudentAuthService} from "./student-auth.service";
import {GameCategoryService} from "./game-category.service";
import {Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {AppState} from "../store";
import {setActiveGameCategoryId} from "../store/actions/student.actions";
import {
  GameCategoriesSelectorFooterStudentComponent
} from "../shared/game-categories-selector/game-categories-selector-footer-student/game-categories-selector-footer-student.component";
import {OrganizationGameCategoryData} from "../interfaces/organization-game-category-data";
import {EGame} from "../enums/game";
import {BehaviorSubject, Observable, take} from "rxjs";
import {LargeMessageService} from "./large-message.service";

@Injectable({
  providedIn: 'root'
})
export class GamesService {

  private _isUserInGameListener = new BehaviorSubject<boolean>(false);

  private _games: Game[] = [];

  // Category data preloaded per game when the start message is shown
  private _studentGameCategoryDataLoading = new Map<EGame, Promise<OrganizationGameCategoryData>>();
  // Global training state (no class game timer running) preloaded per game when the start message is shown
  private _studentGameTrainingLoading = new Map<EGame, Promise<boolean>>();
  constructor(
    private imageService: ImageService,
    private overlayService: OverlayService,
    private data: DataService,
    private studentAuthService: StudentAuthService,
    private gameCategoryService: GameCategoryService,
    private router: Router,
    private store: Store<AppState>,
    private largeMessageService: LargeMessageService
  ) {
    this._games = [
      {
        id: EGame.QUIZ,
        name: "Wer wird Berufs-Champion?",
        description: "Teste dein Wissen in unterschiedlichen Bereichen und werde Berufs-Champion. ",
        currentOnline: Math.floor(Math.random() * 150),
        properties: {
          isAvailable: true,
          isSingle: true
        },
        routerLink: "/klasse/quiz-show",
        imageSrc: "/assets/img/games/previews/quiz-time.png",
        lottieSrc: "/assets/lottie-animations/game_01.json"
      },
      {
        id: EGame.CLICK_THE_PIC,
        name: "Erkenne den Beruf!",
        description: "Finde heraus, welche Berufe sich hinter den Feldern verstecken.",
        currentOnline:  Math.floor(Math.random() * 150),
        properties: {
          isAvailable: true,
          isSingle: true
        },
        routerLink: "/klasse/erkenne-das-bild",
        imageSrc: "/assets/img/games/previews/guess-image.png",
        lottieSrc: "/assets/lottie-animations/game_02.json",
      },
      {
        id: EGame.NOTSET,
        name: "Welcher Lehrberuf bin ich?",
        description: "Demnächst verfügbar",
        currentOnline: 0,
        properties: {
          isAvailable: false,
          isSingle: false
        },
        routerLink: "/games/mein-beruf",
        imageSrc: "/assets/img/games/previews/my-profession.png",
        lottieSrc: "/assets/lottie-animations/game_03.json"
      }
    ];
  }


  getGames(): Game[] {
    return this._games;
  }

  getGameNameById(id: EGame): string {
    return this._games.find((el) => {
      return el.id === id;
    })?.name ?? "";
  }

  gamePropertiesToItemTags(game: Game): ItemTag[] {
    const tags: ItemTag[] = [];
    const itemTagColor: ItemTagColor = "game-blue";

    let singleMultiplayerTag = new ItemTag();
    singleMultiplayerTag.setColor(itemTagColor);
    if(game.properties.isSingle) {
      singleMultiplayerTag.setBeforeIcon("profile").setTitle("Alleine").setSize("small");
    } else {
      singleMultiplayerTag.setBeforeIcon("people").setTitle("Gemeinsam").setSize("small");
    }
    tags.push(singleMultiplayerTag);

    if(game.properties.isAvailable) {
      // const onlinePlayersTag = new ItemTag();
      // onlinePlayersTag.setColor(itemTagColor).setTitle(`${game.currentOnline} Personen aktiv`).setBeforeIcon("direction").setSize("small");
      // tags.push(onlinePlayersTag);
    } else {
      const notAvailableTag = new ItemTag();
      notAvailableTag.setColor(itemTagColor).setTitle("Demnächst verfügbar").setBeforeIcon("play").setSize("small");
      tags.push(notAvailableTag);
    }

    return tags;
  }

  getSubmenuItems(): SubmenuItem[] {
    return this._games.map((game) => {
      const tags = this.gamePropertiesToItemTags(game);

      return {
        link: game.routerLink,
        abstract: game.description,
        title: game.name,
        target: "_self",
        tags: tags,
        iconUrl: game.lottieSrc,
        clickCallback: () => {
          this.startGameSetup(game.id);
        }
      }
    })
  }

  async getGameCurrentOnline(game: EGame): Promise<number> {
    return new Promise(resolve => {
      this.data.getGameActiveUsers(game).pipe(take(1)).subscribe((response) => {
        resolve(response);
      })
    })
  }

  getLargeMessageOnStartByGameId(id: EGame): LargeMessageInput {
    const clientID = this.studentAuthService.getClientId();
    this._studentGameCategoryDataLoading.set(id, this.gameCategoryService.loadStudentGameCategoryData(clientID, id));
    // getClassData() throws without a logged-in student (e.g. start message opened from a teacher quest link)
    if(this.studentAuthService.isLoggedIn()) {
      this._studentGameTrainingLoading.set(id, this.loadIsGlobalTraining());
    }

    switch (id) {
      case EGame.QUIZ:
        return {
          theme: LargeMessageTheme.BLUE,
          text: "Teste dein Wissen in unterschiedlichen Bereichen und werde Berufs-Champion. Setze deine Joker gekonnt ein und knacke den Highscore deiner Klasse.",
          primaryButtonLabel: "Weiter",
          // secondaryButtonLabel: "Spielanleitung ansehen",
          // secondaryButtonCallback: () => {
          //   alert("Spielanleitung");
          // },
          primaryButtonCallback: () => this.openGameCategorySelectionForStudent(id),
          headline: "<p>Wer wird</p><em>Berufs-Champion?</em>",
          lottiePath: "/assets/lottie-animations/game_01_overlay.json",
          lottieLargeScale: false
        }
      case EGame.CLICK_THE_PIC:
        return {
          theme: LargeMessageTheme.BLUE,
          text: "Lasse die Kacheln aufdecken und erkenne den dahinter liegenden Beruf. Je schneller du den Beruf erkennst, desto mehr Punkte räumst du ab.",
          primaryButtonLabel: "Weiter",
          // secondaryButtonLabel: "Spielanleitung ansehen",
          // secondaryButtonCallback: () => {
          //   alert("Spielanleitung");
          // },
          primaryButtonCallback: () => this.openGameCategorySelectionForStudent(id),
          headline: "<p>Erkenne</p><em>den Beruf</em>",
          lottiePath: "/assets/lottie-animations/game_02_overlay.json",
          lottieLargeScale: false
        }
      case EGame.NOTSET:
        return {
          theme: LargeMessageTheme.BLUE,
          text: "Egal ob mit Lehre, weiterführender Schule oder Studium – in jedem Beruf sind unterschiedliche Anforderungen von großer Bedeutung. Welche das sind und welche zu dir passen könnten, kannst du hier herausfinden.",
          primaryButtonLabel: "Weiter",
          secondaryButtonLabel: "Spielanleitung ansehen",
          secondaryButtonCallback: () => {
            // alert("Spielanleitung");
          },
          primaryButtonCallback: () => {
            // alert("Open categories selection for game " + id);
          },
          headline: "<p>Berufsreise</p><em>Quiz Show</em>",
          lottiePath: "/assets/lottie-animations/game_03_overlay.json",
          lottieLargeScale: false
        }
      default:
        throw new Error("Unknown game id");
    }
  }

  async openGameCategorySelectionForStudent(gameId: EGame) {
    if(this.studentAuthService.isLoggedIn()) {
      try {
        const clientID = this.studentAuthService.getClientId();

        // Wait for the preload of this game (if any) so data of another game or stale data is never shown
        const loading = this._studentGameCategoryDataLoading.get(gameId)
          ?? this.gameCategoryService.loadStudentGameCategoryData(clientID, gameId);
        this._studentGameCategoryDataLoading.delete(gameId);

        let studentGameCategoryData = await loading;

        // Only use base Categories for Click the Pic (IDs 1 - 9)
        if(gameId === "CLICKTHEPIC") {
          studentGameCategoryData = {
            ...studentGameCategoryData,
            items: studentGameCategoryData.items.filter(item => item.categoryData.id < 10)
          };
        }

        // Same as category data: use the preload started with the start message, if any
        const isGlobalTraining = await (this._studentGameTrainingLoading.get(gameId) ?? this.loadIsGlobalTraining());
        this._studentGameTrainingLoading.delete(gameId);

        const overlayId = this.overlayService.openWindowOverlay({
          theme: "blue",
          title: "Welche Kategorie möchtest du spielen?",
          componentFooter: {
            component: GameCategoriesSelectorFooterStudentComponent,
            data: {}
          },
          componentMain: {
            component: GameCategoriesSelectorComponent,
            data: {
              data: studentGameCategoryData,
              isTraining: isGlobalTraining,
              mode: "student",
              gameId: gameId,
              selectCallback: (catId: number) => {
                if(studentGameCategoryData) {
                  const targetCategoryData = studentGameCategoryData.items.find(category => category.categoryData.id === catId);
                  const targetGameData = this._games.find((game) => game.id === gameId);

                  if(targetCategoryData && targetGameData) {
                    // alert(`Run game ${targetGameData.name} with category id ${targetCategoryData.categoryData.title}`);
                    this.store.dispatch(setActiveGameCategoryId({gameCategoryId: targetCategoryData.categoryData.id}));
                    this.router.navigateByUrl(targetGameData.routerLink, {});
                    this.overlayService.removeComponent(overlayId);
                  } else {
                    throw new Error("Game or category data is empty...");
                  }
                }

              }
            }
          }
        })
      } catch (error: any) {
        console.warn("Trying to open game category selection without logged in");
      }
    }
  }

  private loadIsGlobalTraining(): Promise<boolean> {
    return new Promise((resolve) => {
      this.data.getGameTimer(this.studentAuthService.getClassData().id).pipe(take(1)).subscribe((response) => {
        resolve(response === false);
      })
    });
  }

  startGameSetup(gameId: EGame) {
    this.largeMessageService.showLargeMessage(this.getLargeMessageOnStartByGameId(gameId));
  }

  setIsUserInGame(isUserInGame: boolean) {
    this._isUserInGameListener.next(isUserInGame);
  }

  getIsUserInGameListener(gameId: number): Observable<boolean> {
    return this._isUserInGameListener;
  }

  getIsUserInGame(): boolean {
    return this._isUserInGameListener.value;
  }
}
